import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { ArrowLeft, CloudUpload, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import { Api, apiHost } from '@/lib/utils'

export const Route = createFileRoute('/_auth/orders/bulk_order')({
  component: RouteComponent,
})

const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx']
const ACCEPTED_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]
const TEMPLATE_URL = '/bulk_order_template.csv'

interface BulkOrderResponse {
  status: number
  total: number
  success: number
  failure: number
  results: { client_order_id: string; sheet_row: number; status: number; order?: { id: string; client_order_id: string; state: string } }[]
}

function RouteComponent() {
  const { setPage, token } = useStore(useShallow(state => ({
    setPage: state.setPage,
    token: state.token,
  })))
  const navigate = useNavigate()

  const [dragging, setDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [sheetUrl, setSheetUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setPage('Orders')
  }, [])

  const isValidFile = (file: File) => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_TYPES.includes(file.type)
  }

  const handleDownloadTemplate = () => {
    const a = document.createElement('a')
    a.href = TEMPLATE_URL
    a.download = 'bulk_order_template.csv'
    a.click()
  }

  // Step 2: called when user clicks "Create Orders"
  const createBulkOrders = useMutation({
    mutationFn: (url: string) =>
      Api<BulkOrderResponse>('/webui/order/bulk_create_from_sheet', 'POST', { sheet_url: url }, { token }),
    onSuccess(data) {
      if (data.success === 0) {
        toast.error('Failed to create orders')
      } else {
        toast.success(`${data.success} orders successfully created`)
      }
      setSelectedFile(null)
      setSheetUrl(null)
    },
    onError(error: { message?: string }) {
      toast.error(error.message || 'Failed to create orders')
    },
  })

  // Step 1: triggered automatically when a file is selected via Browse and Upload
  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('bucket_name', 'bulk_orders')
      const resp = await fetch(`${apiHost}/webui/upload_s3_file`, {
        method: 'POST',
        headers: { Token: token },
        body: formData,
      })
      const json = await resp.json()
      if (!resp.ok || !json.url) {
        throw new Error(json.message || json.status || 'Upload failed')
      }
      return json.url as string
    },
    onSuccess(url) {
      setSheetUrl(url)
    },
    onError(error: { message?: string }) {
      toast.error(error.message || 'Failed to upload file')
      setSelectedFile(null)
    },
  })

  const handleFile = (file: File) => {
    if (!isValidFile(file)) {
      toast.error('Only csv, xls & xlsx files are accepted')
      return
    }
    setSelectedFile(file)
    setSheetUrl(null)
    uploadFile.mutate(file)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  return (
    <div className='fixed top-14 left-0 right-0 bottom-0 p-6 overflow-auto'>
      <div className='max-w-4xl mx-auto'>
        <div className='flex items-center gap-3 mb-6'>
          <button
            onClick={() => navigate({ to: '/orders' })}
            className='flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors'
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className='text-2xl font-bold'>Bulk Orders</h1>
        </div>

        <div className='bg-white rounded-xl border border-gray-200 p-6'>
          <div className='flex items-center justify-between mb-5'>
            <h2 className='text-lg font-semibold'>Import Bulk Orders</h2>
            <Button variant='outline' onClick={handleDownloadTemplate}>
              <Download size={16} />
              Download Template
            </Button>
          </div>

          <div
            onDragOver={e => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-16 gap-4 cursor-pointer transition-colors ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload size={40} className='text-blue-600' strokeWidth={1.5} />
            {selectedFile ? (
              <p className='text-sm font-medium text-gray-700'>{selectedFile.name}</p>
            ) : (
              <p className='text-sm text-gray-500'>Drag And Drop to upload the files here.</p>
            )}
            <p className='text-xs text-gray-400'>OR</p>
            <Button variant='blue' onClick={e => { e.stopPropagation(); fileInputRef.current?.click() }}>
              Browse and Upload
            </Button>
            <p className='text-xs text-gray-500'>
              Only <strong>csv, xls & xlsx</strong> file format will be accepted.
            </p>
            <input
              ref={fileInputRef}
              type='file'
              accept='.csv,.xls,.xlsx'
              className='hidden'
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) handleFile(file)
                e.target.value = ''
              }}
            />
          </div>

          {selectedFile && (
            <div className='mt-4 flex items-center justify-end gap-3'>
              {uploadFile.isPending && (
                <p className='text-sm text-gray-500'>Uploading file...</p>
              )}
              <Button
                variant='blue'
                size='lg'
                loading={createBulkOrders.isPending}
                disabled={!sheetUrl || uploadFile.isPending}
                onClick={() => sheetUrl && createBulkOrders.mutate(sheetUrl)}
              >
                Create Order(s)
              </Button>
            </div>
          )}
        </div>

        <div className='mt-5 bg-amber-50 rounded-xl border border-amber-100 p-5'>
          <h3 className='font-semibold mb-3'>Instructions</h3>
          <ul className='list-disc list-inside text-sm text-gray-700 space-y-2'>
            <li>Download the sample file and replace its data with your order data. Save the file and upload it back.</li>
            <li>Make sure all mandatory fields are filled in the sheet.</li>
            <li>Only <strong>csv, xls & xlsx</strong> formats are supported.</li>
            <li>Each row in the file represents one order.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
